import * as React from "react";

import "./ad-budget-defaults-route.scss";

import {cs}                from "@Common/react/chain-services";
import {consumeContext}    from "@Common/react/context";
import {Load}              from "@Common/react/load";
import {Load2}             from "@Common/react/load2";
import {scnv}              from "@Common/react/state-change-nested-value";
import {UseState}          from "@Common/react/use-state";
import {ConfirmDialog}     from "@Common/ui-components/heavy/common/design2/dialogs/confirm-dialog/confirm-dialog";
import {InformationBadge}  from "@Common/ui-components/heavy/common/design2/information-badge/information-badge";
import {needsConfirmation} from "@Common/ui-components/needs-confirmation/needs-confirmation";
import {Responsive}        from "@Common/ui-components/responsive";

import {AccountingLayout} from "../common/accounting-layout";
import {OfficeSelect}     from "../common/office-select/office-select";
import {UpdateAmountForm} from "../common/update-amount-form/update-amount-form";

import {AdBudgetsDefaultsTable} from "./ad-budget-table/ad-budget-table";
import {AdBudgetDefaultsCardContainer} from "./ad-budget-defaults-card-container/ad-budget-defaults-card-container";

export const AccountAdminAdBudgetDefaultsRoute = () => cs(
    ({}, next) => AccountingLayout({activeTab: "ad-budget-defaults", next}),
    consumeContext("apis"),
    ["branchList", ({apis}, next) => Load({
        fetch: () => apis.next.getBranchList(),
        next,
    })],
    ["adBudgets", ({apis, branchList}, next) => Load2({
        _key: !!branchList,
        fetch: () => apis.next.adBudgets.getAdBudgetDefaults(branchList?.map((v) => ({idBranch: v.idBranch}))),
        next,
    })],
    ["selectedBranches", ({}, next) => UseState({
        initValue: [],
        next,
    })],
    ["confirmDialog", ({}, next) => ConfirmDialog({next})],
    ["responsive", ({}, next) => <Responsive {...{
        sizes: {
            md: 1024,
        },
        next,
    }}/>], 
    ({apis, adBudgets, branchList, selectedBranches, confirmDialog, responsive}) => (
        <div className="ad-budget-defaults-route-fr6">
            {DefaultsHeader({defaultAdBudget: adBudgets.value?.DEFAULT?.amount})}            
            {InputContainer({apis, adBudgets, branchList, selectedBranches, confirmDialog})}
            {
                responsive.ge("md") ? (
                    <div className="ad-budget-table-container">
                        {AdBudgetsDefaultsTable({
                            branches: selectedBranches.value,
                            getAmount: (branch) => {
                                if(adBudgets.loading) {
                                    return 0
                                }
                                return adBudgets.value[branch.idBranch].amount
                            },
                            setAmount: (idBranch, newAmount) => {
                                scnv(adBudgets, [idBranch, "amount"], () => newAmount);
                            },
                        })}
                    </div>
                ) : (
                    AdBudgetDefaultsCardContainer({adBudgets, selectedBranches})
                )
            }
        </div>
    ),
);

const DefaultsHeader = ({defaultAdBudget}) => (
    <div className="defaults-header">
        <span className="company-default-label" xd-qa-type="label" xd-qa-name="Company Default">Company Default:</span>
        <span className="company-default-limit" xd-qa-type="text" xd-qa-name="Amount">${defaultAdBudget}</span>
        {InformationBadge({
            height: 20,
            getTooltip: () => "Contact Xpressdocs in order to modify the company-wide default",
            xdQaName: "Contact Xpressdocs"
        })}
    </div>
);

const InputContainer = ({apis, adBudgets, branchList, confirmDialog, selectedBranches}) => (
    <div className="input-container">
        <div className="left">
            {OfficeSelect({branchList, selectedBranches})}
        </div>

        <div className="right">
            {UpdateAmountForm({
                onChange: async (v) => {
                    const proceed = await needsConfirmation({
                        confirmDialog,
                        actionName: "update Ad Budget defaults for the selected office(s)",
                        message: "Update Ad Budget Defaults",
                    })
                    if(proceed) {
                        await apis.next.adBudgets.setAdBudgetDefaults(selectedBranches.value.map(b => ({
                            idBranch: b.idBranch,
                            amount: v,
                        })));

                        for (const branch of selectedBranches.value) {
                        scnv(adBudgets, [branch.idBranch, "amount"], () => v);
                    }
                    } 
                },
                disabled: selectedBranches.value.length === 0,
            })}
        </div>
    </div>
);

export const AD_BUDGET_PLACEHOLDER = "Select one or multiple offices to adjust their Ad Budget defaults."
